/**
 * IS THIS LINK A PLACE INSIDE THE APP?
 *
 * Links the app writes about itself - a meeting, a channel, a calendar entry -
 * are stored ABSOLUTE (`https://teams.example.com/meet/<id>`), so the same
 * string still works pasted into an e-mail. Drawn as an ordinary anchor, that
 * is a full document load in a new window: the SPA boots a second time, and in
 * the desktop client the "new window" is the SYSTEM BROWSER, where the person
 * is usually not signed in. So a link whose origin is this app's own is turned
 * back into a path and handed to the router.
 *
 * NOT EVERYTHING ON THIS ORIGIN IS A ROUTE. `/v1/...` is the API (a file
 * download lives there), `/ws` is the socket, and anything that ends in a file
 * extension is a file. Those stay ordinary links.
 *
 * NOR IS A PATH THAT BEGINS `//`. `https://<this app>//elsewhere.example/x` is
 * on this origin - and its path, handed to the router, is
 * `//elsewhere.example/x`: a protocol-relative URL, which a link treats as
 * another site and FOLLOWS, in this window. Anybody can post one. It would have
 * taken the app's own window (in the desktop client, the app itself) to a page
 * of their choosing, from a link that reads as this workspace's own address.
 * The test is on the PARSED path, so `/\elsewhere` and `/.//elsewhere` - which
 * the parser turns into the same thing - are caught with it.
 *
 * `URL` throws on anything unparseable; that is an outbound link too.
 */
const NOT_A_ROUTE: readonly RegExp[] = [/^\/v1(\/|$)/, /^\/ws(\/|$)/, /^\/assets\//, /\.[a-z0-9]{2,5}$/i];

export function inAppPath(href: string, origin: string = window.location.origin): string | null {
  try {
    const url = new URL(href, origin);
    if (url.origin !== origin) return null;
    if (url.pathname.startsWith('//')) return null;
    if (NOT_A_ROUTE.some((pattern) => pattern.test(url.pathname))) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}
